import React from 'react'
import * as moment from 'moment'
import { DayPickerRangeController } from 'react-dates'

import * as constants from '../../../../../static/constants'
import styled from 'styled-components'
import { pushDataLayer } from 'utils/googleTagManager'
import DateRangePickerCustomStyle from 'static/dateRangePickerCustomStyle'
import { Button } from 'components/atoms'
import { ReservationDateGuest } from './ReservationDateGuest'
import { IAges, ISelectedDate, ISelectedGuests } from 'utils/reservation'
import { GuestsCounter } from './GuestsCounter'
import { ICustomPost } from 'static/interfaces'
import { ICalendarItem } from 'utils/useCalendarEvents'
import { updateSearchFiltersCookie } from 'utils/searchFiltersCookie'
import {
  getReservationStorageData,
  updateReservationStorageData,
} from 'utils/reservationSessionStorage'

interface IMultiDatePickerProps {
  post: ICustomPost
  isPrivate: boolean
  initialDate?: moment.Moment
  dates: moment.Moment[]
  guests: ISelectedGuests
  guestCount: number
  calendarEvents: ICalendarItem[]
  ages: IAges
  setDates: React.Dispatch<React.SetStateAction<moment.Moment[]>>
  setGuests: React.Dispatch<React.SetStateAction<ISelectedGuests>>
  getCalendarEvents: (yearMonth: any) => Promise<void>
}

export const ReservationMultiDatePicker: React.VFC<IMultiDatePickerProps> = ({
  post,
  initialDate,
  dates,
  guests,
  guestCount,
  calendarEvents,
  ages,
  setDates,
  setGuests,
  getCalendarEvents,
}) => {
  const [isOpenDatePicker, setIsOpenDatePicker] = React.useState(false)
  const [isOpenGuestCount, setIsOpenGuestCount] = React.useState(false)
  // 最大予約可能人数でリミット
  const isPrivate = post.post_type === 'private'

  // Reset children and infants counts for private bookings on first load
  React.useEffect(() => {
    if (isPrivate && (guests.children > 0 || guests.infants > 0)) {
      const resetGuests = {
        adults: guests.adults + (guests.children ?? 0),
        children: 0,
        infants: 0,
      }
      setGuests(resetGuests)
      handleUpdateLocalDataByKey('guests', resetGuests)
    }
  }, [isPrivate]) // Only run when isPrivate changes or on mount

  const [selectedDates, setSelectedDates] = React.useState<ISelectedDate>(() => {
    return {
      startDate: dates.length > 0 ? dates[0] : null,
      endDate: dates.length > 0 ? dates[dates.length - 1] : null,
    }
  })

  React.useEffect(() => {
    if (dates.length === 0) {
      setSelectedDates({
        startDate: null,
        endDate: null,
      })
    } else {
      setSelectedDates({
        startDate: dates[0],
        endDate: dates[dates.length - 1],
      })
    }
  }, [dates])

  const [focusedInput, setFocusedInput] = React.useState<null | 'startDate' | 'endDate'>(
    'startDate'
  )

  const onDatesChange = ({ startDate, endDate }: ISelectedDate) => {
    setSelectedDates({
      startDate,
      endDate: focusedInput === 'startDate' ? startDate : endDate,
    })
  }

  const handleUpdateLocalDataByKey = (key: 'dates' | 'guests', value: any) => {
    const reservationStorageData = getReservationStorageData()
    const nextDates = key === 'dates' ? value : dates
    const nextGuests = key === 'guests' ? value : guests
    // 日付はISO文字列で保存
    const datesForStorage =
      nextDates && Array.isArray(nextDates)
        ? nextDates.map(d => (d && typeof d.format === 'function' ? d.format('YYYY-MM-DD') : d))
        : []
    updateReservationStorageData({
      initParams: {
        ...reservationStorageData.initParams,
        [key]: key === 'dates' ? datesForStorage : value,
      },
    })
    updateSearchFiltersCookie({
      ...(datesForStorage.length > 0 ? { date: datesForStorage[0] } : {}),
      dateRange:
        datesForStorage.length > 1
          ? {
              startDate: datesForStorage[0],
              endDate: datesForStorage[datesForStorage.length - 1],
            }
          : null,
      guests: nextGuests,
    })
  }

  const applyDates = React.useCallback(() => {
    if (selectedDates.startDate && selectedDates.endDate) {
      const diff = selectedDates.endDate.diff(selectedDates.startDate, 'days')
      const newDates = []
      if (diff === 0) {
        newDates.push(selectedDates.startDate)
      } else {
        for (let i = 0; i <= diff; i++) {
          const newDate = moment(selectedDates.startDate).add(i, 'days')
          newDates.push(newDate)
        }
      }
      handleUpdateLocalDataByKey('dates', newDates)
      setDates(newDates)
      pushDataLayer({
        event: 'click_tour_date',
        tour_date: selectedDates.startDate.format('YYYY/MM/DD'),
      })
      pushDataLayer({
        event: 'click_tour_date',
        tour_date: selectedDates.endDate.format('YYYY/MM/DD'),
      })
    } else if (!selectedDates.startDate && !selectedDates.endDate) {
      handleUpdateLocalDataByKey('dates', [])
      setDates([])
    }
    setFocusedInput('startDate')
    setIsOpenDatePicker(false)
  }, [calendarEvents, selectedDates.startDate, selectedDates.endDate, guestCount])

  const onDateRangePickerMonthChange = async (changedMoment: moment.Moment) => {
    onDatesChange({ startDate: null, endDate: null })
    handleUpdateLocalDataByKey('dates', [])
    setDates([])
    getCalendarEvents(changedMoment.startOf('month'))
  }

  // 日付キー('YYYY-MM-DD')でO(1)参照できるMapを1回だけ構築する。
  // react-dates は表示中の全日セル × 再レンダーごとに下記コールバックを呼ぶため、
  // calendarEvents の線形探索(最大~180件) + moment.isSame を避ける。
  const calendarEventMap = React.useMemo(() => {
    const map = new Map<string, { hasSlots: boolean; fullyBooked: boolean }>()
    calendarEvents?.forEach(calendarEvent => {
      const hasSlots = calendarEvent.start_times.length > 0
      map.set(calendarEvent.date.format('YYYY-MM-DD'), {
        hasSlots,
        fullyBooked:
          hasSlots &&
          calendarEvent.start_times.every(
            start_time => start_time.available_count === 0 || start_time.reserved
          ),
      })
    })
    return map
  }, [calendarEvents])

  const handleCalendarOutSideRange = React.useCallback(
    (calendarMoment: moment.Moment) => {
      return !calendarEventMap.get(calendarMoment.format('YYYY-MM-DD'))?.hasSlots
    },
    [calendarEventMap]
  )

  const fullyBooked = React.useCallback(
    (date: moment.Moment) => {
      const entry = calendarEventMap.get(date.format('YYYY-MM-DD'))
      if (!entry || !entry.hasSlots) {
        return undefined
      }
      return entry.fullyBooked
    },
    [calendarEventMap]
  )

  const isMobile = matchMedia(`(max-width: ${constants.BREAKPOINT_TABLET_SMALL}px)`).matches
  return (
    <ReservationDatePicker>
      <ReservationDateGuest
        selectedDates={selectedDates}
        guestCount={guestCount}
        setIsOpenDatePicker={setIsOpenDatePicker}
        setIsOpenGuestCount={setIsOpenGuestCount}
      />
      {isOpenDatePicker && (
        <div className="DayPickerWrapper">
          <DateRangePickerCustomStyle />
          <DayPickerRangeController
            minimumNights={0}
            noBorder={true}
            startDate={selectedDates.startDate}
            endDate={selectedDates.endDate}
            onDatesChange={onDatesChange}
            initialVisibleMonth={() => initialDate}
            enableOutsideDays={false}
            numberOfMonths={isMobile ? 1 : 2}
            hideKeyboardShortcutsPanel={true}
            isOutsideRange={handleCalendarOutSideRange}
            onPrevMonthClick={onDateRangePickerMonthChange}
            onNextMonthClick={onDateRangePickerMonthChange}
            focusedInput={focusedInput}
            onFocusChange={(currentFocusedInput: null | 'startDate' | 'endDate') =>
              setFocusedInput(currentFocusedInput || 'startDate')
            }
            isDayHighlighted={fullyBooked}
          />
          <div className="Buttons DatepickerButtons">
            <Button
              handleClick={() => {
                handleUpdateLocalDataByKey('dates', [])
                onDatesChange({ startDate: null, endDate: null })
                setDates([])
                setIsOpenDatePicker(false)
              }}
              large={true}
              textColor={constants.COLORS.subText}
              borderColor={constants.COLORS.divider}
            >
              Reset
            </Button>
            <Button primary={true} handleClick={applyDates} large={true}>
              Save
            </Button>
          </div>
        </div>
      )}
      {isOpenGuestCount && (
        <GuestsCounter
          post={post}
          isPrivate={isPrivate}
          guests={guests}
          ages={ages}
          setGuests={setGuests}
          setIsOpenGuestCount={setIsOpenGuestCount}
          handleUpdateLocalDataByKey={handleUpdateLocalDataByKey}
        />
      )}
    </ReservationDatePicker>
  )
}

const ReservationDatePicker = styled.div`
  position: relative;
  width: 100%;
  flex: 0 0 auto;
  .PostShow_StickyBox_content & {
    width: 100%;
  }
  @media (max-width: ${constants.BREAKPOINT_DESKTOP}px) {
    margin-left: auto;
    margin-right: auto;
  }
  @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
    width: 100%;
  }
  .DayPickerWrapper {
    position: absolute;
    z-index: 10;
    background-color: ${constants.COLORS.white};
    padding: 0.8rem;
    border-radius: 1.2rem;
    box-shadow: ${constants.COLORS.cardShadow};
    .PostShow_StickyBox_content & {
      right: 0;
    }
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      margin-left: 1.6rem;
    }
    @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      margin-left: 0;
      > div {
        margin: 0 auto;
      }
    }
  }
  .DatepickerButtons {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 0.4rem 2rem 1.6rem 0;
    @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      padding: 0.4rem 0 1.6rem;
      justify-content: center;
    }
    button {
      width: 11rem;
      & + button {
        margin-left: 1.6rem;
      }
    }
  }
`
